import { useState, useEffect, useRef, useMemo } from "react";
import axiosInstance from '../axiosInstance';

export const useSubFormApproval = ({ enduserFilledFormId, subForms, persona = null }) => {
  const checkLink = persona === "customer" ? "check/enduser" : "check";
  
  // State for managing subforms with approval status
  const [localSubForms, setLocalSubForms] = useState(() => {
    if (!subForms || subForms.length === 0) return [];
    return subForms.map(form => ({ ...form, approved: 0 }));
  });
  const [isCheckingApprovals, setIsCheckingApprovals] = useState(true);
  
  // State for API operations
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  
  // use ref to track if we've already fetched for this enduserFilledFormId
  const fetchedForIdRef = useRef(null);
  
  // use ref to track previous subForms to prevent unnecessary state updates
  const prevSubFormsRef = useRef([]);

  // create a stable key for subForms to detect real changes
  const subFormsKey = useMemo(() => {
    if (!subForms || subForms.length === 0) return '';
    return subForms.map(f => f.id).sort().join(',');
  }, [subForms]);

  // Update localSubForms when subForms prop changes
  useEffect(() => {
    const currentIds = localSubForms?.map(f => f.id).sort().join(',') || '';
    const newIds = subForms?.map(f => f.id).sort().join(',') || '';
    
    if (currentIds !== newIds && subForms) {
      // Reset to not approved when subForms change
      setLocalSubForms(subForms.map(form => ({ ...form, approved: 0 })));
      // Clear fetch cache to force re-fetch
      fetchedForIdRef.current = null;
    }
  }, [subForms, localSubForms]);

  // Fetch approval status on mount and when dependencies change
  useEffect(() => {
    const fetchApprovals = async () => {
      if (!subForms || subForms.length === 0 || !enduserFilledFormId) {
        setIsCheckingApprovals(false);
        return;
      }

      // Skip if we've already fetched for this ID and subforms combination
      const fetchKey = `${enduserFilledFormId}-${subFormsKey}`;
      if (fetchedForIdRef.current === fetchKey) {
        setIsCheckingApprovals(false);
        return;
      }

      setIsCheckingApprovals(true);
      
      try {
        const response = await axiosInstance.get(`/api/approve-enduser-filled-forms/${checkLink}`, {
          params: { 
            enduser_filled_form_id: enduserFilledFormId, 
            subform_id: null // Get all approvals
          }
        });
        
        if (response.data && response.data.approved_subform_ids) {
          const updatedSubForms = subForms.map((form) => ({
            ...form,
            approved: response.data.approved_subform_ids.includes(form.id) ? 1 : 0
          }));
          setLocalSubForms(updatedSubForms);
          fetchedForIdRef.current = fetchKey;
        } else {
          // No approvals found, ensure all are set to not approved
          const updatedSubForms = subForms.map((form) => ({
            ...form,
            approved: 0
          }));
          setLocalSubForms(updatedSubForms);
          fetchedForIdRef.current = fetchKey;
        }
      } catch (error) {
        console.error("Error checking approvals:", error);
        // On error, ensure all are set to not approved
        const updatedSubForms = subForms.map((form) => ({
          ...form,
          approved: 0
        }));
        setLocalSubForms(updatedSubForms);
      } finally {
        setIsCheckingApprovals(false);
      }
    };

    fetchApprovals();
  }, [enduserFilledFormId, subFormsKey, checkLink, subForms]);

  const approveSubForm = async (subformId) => {
    if (!enduserFilledFormId || !subformId) {
      const error = new Error("Something went wrong.");
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsApproving(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.post(`/api/approve-enduser-filled-forms/approve`, {
         enduser_filled_form_id: enduserFilledFormId,
         subform_id: subformId
      });

      if (response.status === 200 || response.status === 201) {
        // optimized state update - only update if approval status actually changed
        setLocalSubForms(prevSubForms => {
          const existingForm = prevSubForms.find(f => f.id === subformId);
          // if already approved, don't update state
          if (existingForm && existingForm.approved === 1) {
            return prevSubForms;
          }
          return prevSubForms.map((form) =>
            form.id === subformId ? { ...form, approved: 1 } : form
          );
        });
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsApproving(false);
    }
  };

  const deleteApproval = async (subformId) => {
    if (!enduserFilledFormId || !subformId) {
      const error = new Error("Something went wrong.");
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsDeleting(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.delete(`/api/approve-enduser-filled-forms/delete`, {
        data: {
          enduser_filled_form_id: enduserFilledFormId,
          subform_id: subformId
        }
      });

      if (response.status === 200) {
        // optimized state update - only update if approval status actually changed
        setLocalSubForms(prevSubForms => {
          const existingForm = prevSubForms.find(f => f.id === subformId);
          // if already not approved, don't update state
          if (existingForm && existingForm.approved === 0) {
            return prevSubForms;
          }
          return prevSubForms.map((form) =>
            form.id === subformId ? { ...form, approved: 0 } : form
          );
        });
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  // Get approval status for a specific subform
  const getSubFormApprovalStatus = (subformId) => {
    const subform = localSubForms.find(f => f.id === subformId);
    return !!subform?.approved;
  };

  return {
    // State
    subForms: localSubForms,
    isCheckingApprovals,
    isApproving,
    isDeleting,
    approvalError,
    
    // Actions
    approveSubForm,
    deleteApproval,
    getSubFormApprovalStatus,
  };
};
